<template>
  <span>
    <v-dialog max-width="1000px" persistent :value="true">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("travelService.orderHotel") }}</span>
        </v-card-title>
        <v-card-text>
          <v-form lazy-validation ref="form" v-model="formValid">
            <multiple-user-select
              :birth-date-required="$store.getters['userProperties'].system_settings.travel_service_hotel_insurance === insuranceTypeID.INSURANCE_ADD"
            />
            <v-row dense>
              <v-col class="pr-2" cols="6">
                <v-select
                  :items="hotelTypes()"
                  :label="$t('travelService.roomType') + '*'"
                  :rules="[validatorIsRequired]"
                  v-model="contextModel.travelServiceOrderItemHotel.room_type"
                />
              </v-col>
              <v-col class="pl-2" cols="6">
                <location-ajax-select
                  :label="$t('travelService.place') + '*'"
                  :placeholder="$t('travelService.enterCountry')"
                  :rules="[v => (v != null && v.length > 0) || $t('global.error.required')]"
                  :select-model.sync="contextModel.travelServiceOrderItemHotel.place_id"
              /></v-col>
            </v-row>
            <v-row dense>
              <v-col class="pr-2" cols="6">
                <date-picker
                  :label="$t('travelService.accommodationDate') + '*'"
                  :max="contextModel.travelServiceOrderItemHotel.end_date"
                  :model.sync="contextModel.travelServiceOrderItemHotel.start_date"
                  :rules="[validatorIsRequired, validatorIsFutureDate]"
                />
              </v-col>
              <v-col class="pl-2" cols="6">
                <v-tooltip bottom>
                  {{ $t("travelService.accommodationHourTooltip") }}
                  <template #activator="{on}">
                    <time-picker
                      v-on="on"
                      :label="$t('travelService.accommodationTime')"
                      :model.sync="contextModel.travelServiceOrderItemHotel.start_time"
                      placeholder="14:00 - 18:00"
                      prepend-icon="fa-info-circle"
                      clearable
                    />
                  </template>
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col class="pr-2" cols="6">
                <date-picker
                  :label="$t('travelService.outDate') + '*'"
                  :min="contextModel.travelServiceOrderItemHotel.start_date"
                  :model.sync="contextModel.travelServiceOrderItemHotel.end_date"
                  :rules="[validatorIsRequired, validatorIsFutureDate]"
                />
              </v-col>
              <v-col cols="6">
                <v-checkbox :label="$t('travelService.isFreeCancellationRequired')" v-model="contextModel.travelServiceOrderItemHotel.is_free_cancellation" />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="6">
                <v-checkbox v-model="contextModel.travelServiceOrderItemHotel.is_breakfast" :label="$t('global.breakfast')" />
              </v-col>
              <v-col cols="6">
                <v-checkbox :label="$t('travelService.isParkingRequired')" v-model="contextModel.travelServiceOrderItemHotel.parking_required" />
              </v-col>
            </v-row>
            <v-checkbox v-model="contextModel.travelServiceOrderItemHotel.is_additional_services" :label="$t('travelService.additionalServices')" />
            <v-text-field
              :label="$t('travelService.additionalServicesDetails')"
              v-model="contextModel.travelServiceOrderItemHotel.additional_services"
              :placeholder="$t('travelService.additionalServicesPlaceholder')"
              v-if="contextModel.travelServiceOrderItemHotel.is_additional_services"
              :rules="[validatorIsRequired]"
            />
            <v-row dense>
              <v-col class="pr-2" cols="6">
                <currency-select :model.sync="contextModel.travelServiceOrderItemHotel.currency" />
              </v-col>
              <v-col class="pl-2" cols="6">
                <cost-centre-select :value.sync="contextModel.cost_centre" />
              </v-col>
            </v-row>
            <v-text-field :label="$t('global.comment')" v-model="contextModel.comment" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click.native="cancel" color="blue darken-1" text>{{ $t("global.cancel") }}</v-btn>
          <v-btn :disabled="!formValid" @click.native="save" color="blue darken-1" text>Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import LocationAjaxSelect from "../CustomInputComponents/LocationAjaxSelect";
import DatePicker from "../CustomInputComponents/DatePicker";
import TimePicker from "../CustomInputComponents/TimePicker";
import MultipleUserSelect from "./MultipleUserSelect";
import CostCentreSelect from "./CostCentreSelect";
import { allRoomTypes } from "../../staticData/TravelServiceTypes";
import CurrencySelect from "../CustomInputComponents/CurrencySelect";
import vue from "../../main";
import moment from "moment";
import { insuranceTypeID } from "../../staticData/TravelServiceTypes";

export default {
  name: "OrderServiceHotelDialog",
  components: {
    CurrencySelect,
    CostCentreSelect,
    MultipleUserSelect,
    TimePicker,
    DatePicker,
    LocationAjaxSelect
  },
  data: () => ({
    formValid: true,
    contextModel: null,
    insuranceTypeID
  }),
  computed: {
    editedTravelServiceOrderItem() {
      return this.$store.state.TravelService.editedTravelServiceItem;
    }
  },
  watch: {
    contextModel: {
      deep: true,
      handler() {
        if (JSON.stringify(this.contextModel) !== JSON.stringify(this.editedTravelServiceOrderItem)) {
          this.updateVuexState();
        }
      }
    },
    editedTravelServiceOrderItem: {
      deep: true,
      handler(val) {
        this.contextModel = JSON.parse(JSON.stringify(val));
      }
    }
  },
  methods: {
    validatorIsRequired(v) {
      return (v != null && v.length > 0) || this.$t("global.error.required");
    },
    validatorIsFutureDate(v) {
      return (
        moment(v)
          .endOf("day")
          .isAfter(moment()) || vue.$t("global.error.dateLaterThanNow")
      );
    },
    updateVuexState() {
      this.$store.commit("TravelService/setEditedTravelServiceItem", this.contextModel);
    },
    hotelTypes: allRoomTypes,
    cancel() {
      this.$store.commit("TravelService/setEditedTravelServiceItem", null);
      this.$store.commit("TravelService/setOldTravelServiceItem", null);
      this.$refs.form.resetValidation();
    },
    warnAboutNotEnoughPerson() {
      if (
        this.contextModel.travelServiceOrderItemHotel.room_type != null &&
        this.hotelTypes().find(t => t.value === this.contextModel.travelServiceOrderItemHotel.room_type).capacity != null &&
        this.hotelTypes().find(t => t.value === this.contextModel.travelServiceOrderItemHotel.room_type).capacity !==
          this.contextModel.travelServiceOrderItemPassengers.length
      )
        return this.$store.dispatch("confirmDialog", {
          content: this.$t("travelService.roomTypeAndSelectedPersonsAreNotTheSameWarning.content"),
          title: this.$t("travelService.roomTypeAndSelectedPersonsAreNotTheSameWarning.title")
        });
      return new Promise((resolve /*, reject*/) => {
        resolve();
      });
    },
    save() {
      if (this.$refs.form.validate()) {
        this.warnAboutNotEnoughPerson().then(() => {
          this.$store.dispatch("TravelService/saveEditedServiceItem");
        });
      }
    }
  },
  created() {
    //this.contextModel.is_insurance = parseInt(this.$store.getters["userProperties"].system_settings.travel_service_hotel_insurance) === 1;
    this.contextModel = JSON.parse(JSON.stringify(this.editedTravelServiceOrderItem));
  }
};
</script>

<style scoped></style>
